import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useDeleteChatConversationMutation } from "@/services/(my-space)/api.chat";
import { getErrorMessage } from "@/utils/error.utils";

/** Confirms "Delete chat" — removes it for me only, never for the others. */
export const DeleteChatDialog = ({
  conversationId,
  isSelf,
  onClose,
  onDeleted,
}: {
  /** Open while set. */
  conversationId: number | null;
  /** My own notes: there is no "everyone else" to keep seeing them. */
  isSelf?: boolean;
  onClose: () => void;
  onDeleted?: (conversationId: number) => void;
}) => {
  const { t } = useTranslation("chat");
  const [deleteChat, { isLoading }] = useDeleteChatConversationMutation();

  const confirm = async () => {
    const id = conversationId;
    if (id == null) return;
    try {
      await deleteChat(id).unwrap();
      toast.success(t("info.deleted_toast"));
      onClose();
      onDeleted?.(id);
    } catch (e) {
      toast.error(getErrorMessage(e, t("errors.delete_chat")));
    }
  };

  return (
    <AlertDialog open={conversationId != null} onOpenChange={(v) => !v && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("info.delete_chat_title")}</AlertDialogTitle>
          <AlertDialogDescription>
            {isSelf ? t("info.delete_self_chat_message") : t("info.delete_chat_message")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("composer.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            disabled={isLoading}
            className="bg-danger text-danger-foreground hover:bg-danger/90"
            onClick={(e) => {
              // Stay open until the request settles (it closes itself on success).
              e.preventDefault();
              void confirm();
            }}
          >
            {t("info.delete_chat")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
